import { useContext, useEffect, useState } from "react";
import AppContext from "../../../context/AppContext";
import TspApiService from "../api/tspApiService";

// Phase 2 originally read `user.tspPermissions` off AppContext, but nothing
// ever populates that field (no login/claims change was made for TSP), so
// the gate would always fail closed with no way to open it. Phase 4 makes
// this hook self-sufficient instead: it fetches the caller's resolved TSP
// role/permissions from GET /api/tsp/me itself, rather than requiring a
// change to the existing login flow or AppContext.
//
// The result is shared: every component that calls this hook (route gate,
// header, pages) used to fire its own /tsp/me request and start in "loading",
// so the header rendered with no permitted menu items until its own request
// finished (a visible menu flicker, and many redundant requests per page).
// Now one request per session token is made and reused for a short time; hook
// instances that mount after it has resolved start already loaded. The hook's
// return shape is unchanged.
const CACHE_TTL_MS = 60 * 1000;
const cache = { token: null, at: 0, promise: null, value: null };

function readCache(token) {
  return cache.token === token && cache.value && Date.now() - cache.at < CACHE_TTL_MS ? cache.value : null;
}

function loadAccess(token) {
  if (cache.token === token && cache.promise && !(cache.value && Date.now() - cache.at >= CACHE_TTL_MS)) return cache.promise;
  cache.token = token;
  cache.value = null;
  cache.promise = TspApiService.getMyTspAccess()
    .then((response) => {
      const data = response?.data || {};
      const value = { role: data.role || null, permissions: data.permissions || [] };
      cache.value = value;
      cache.at = Date.now();
      return value;
    })
    .catch(() => {
      // Fail closed for the callers already waiting, but do NOT cache the failure:
      // the next component to mount retries instead of inheriting "no access".
      cache.promise = null;
      cache.value = null;
      return { role: null, permissions: [] };
    });
  return cache.promise;
}

export default function useTspPermission() {
  const { user, token } = useContext(AppContext);
  const [state, setState] = useState(() => {
    const cached = user && token ? readCache(token) : null;
    return cached ? { ...cached, loading: false } : { role: null, permissions: [], loading: Boolean(user && token) };
  });

  useEffect(() => {
    let cancelled = false;

    if (!user || !token) {
      setState({ role: null, permissions: [], loading: false });
      return undefined;
    }

    const cached = readCache(token);
    if (cached) {
      setState({ ...cached, loading: false });
      return undefined;
    }

    setState((prev) => ({ ...prev, loading: true }));
    loadAccess(token).then((value) => {
      if (!cancelled) setState({ ...value, loading: false });
    });

    return () => {
      cancelled = true;
    };
  }, [user, token]);

  const hasPermission = (permissionKey) => state.permissions.includes(permissionKey);
  const hasAnyTspAccess = () => state.permissions.length > 0;

  return { role: state.role, permissions: state.permissions, loading: state.loading, hasPermission, hasAnyTspAccess };
}
